import { useCallback, useEffect, useState } from 'react';
import { chatApi } from '@/api';

export const LESSON_CHAT_BLOCK_TYPE = 'lesson-chat';

const firstText = (...values) => values.find(
  (value) => typeof value === 'string' && value.trim(),
)?.trim() || '';

const firstId = (...values) => values.find(
  (value) => value !== undefined && value !== null && value !== '',
);

const findById = (items = [], id) => (
  id === undefined
    ? null
    : items.find((item) => Number(item?.id) === Number(id)) ?? null
);

export const isGeneralLessonChat = (chat) => (
  (chat?.lesson_chat_kind ?? chat?.lessonChatKind) === 'general'
);

export const withLessonChatContext = (chat, filters = {}) => {
  const lessonId = firstId(
    chat?.lessonId,
    chat?.lesson_id,
    chat?.lesson?.id,
    chat?.context?.lessonId,
    chat?.context?.lesson_id,
  );
  const lesson = findById(filters?.lessons, lessonId);
  const themeId = firstId(
    chat?.themeId,
    chat?.theme_id,
    chat?.theme?.id,
    chat?.context?.themeId,
    chat?.context?.theme_id,
    lesson?.themeId,
    lesson?.theme_id,
  );
  const theme = findById(filters?.themes, themeId);
  const lessonName = firstText(
    chat?.lessonName,
    chat?.lesson_name,
    chat?.lesson?.name,
    chat?.context?.lessonName,
    chat?.context?.lesson_name,
    lesson?.name,
  );
  const themeName = firstText(
    chat?.themeName,
    chat?.theme_name,
    chat?.theme?.name,
    chat?.context?.themeName,
    chat?.context?.theme_name,
    theme?.name,
  );

  return {
    ...chat,
    lessonChatContext: {
      ...(chat?.lessonChatContext ?? {}),
      lessonId,
      lessonName,
      themeId,
      themeName,
    },
  };
};

export const getLessonChatDisplayTitle = (chat) => {
  const lessonName = firstText(
    chat?.lessonChatContext?.lessonName,
    chat?.lessonName,
    chat?.lesson_name,
    chat?.lesson?.name,
    chat?.context?.lessonName,
    chat?.context?.lesson_name,
  );
  const themeName = firstText(
    chat?.lessonChatContext?.themeName,
    chat?.themeName,
    chat?.theme_name,
    chat?.theme?.name,
    chat?.context?.themeName,
    chat?.context?.theme_name,
  );
  const contextTitle = [
    lessonName && `урок «${lessonName}»`,
    themeName && `тема «${themeName}»`,
  ].filter(Boolean).join(' — ');
  const chatTitle = isGeneralLessonChat(chat)
    ? ''
    : firstText(chat?.shortName, chat?.group?.name, chat?.name);

  return [chatTitle, contextTitle].filter(Boolean).join(' — ')
    || firstText(chat?.shortName, chat?.name)
    || 'Чат урока';
};

export const isLessonChatsRoute = ({ pathname = '', lessonId = null }) => {
  if (pathname === '/preview-lesson') return true;
  if (!lessonId) return false;

  return pathname.startsWith('/v2/courses/my/course-or-group-')
    || /^\/courses\/my\/[^/]+\/[^/]+\/topic-/.test(pathname);
};

export const normalizeLessonChatsResponse = (response) => {
  const payload = response?.data;
  const candidates = [
    payload,
    payload?.data,
    payload?.data?.data,
    payload?.items,
    payload?.data?.items,
  ];
  const chats = candidates.find(Array.isArray);

  if (chats) return chats;
  if (payload?.status === 'success') return [];

  return null;
};

const getLessonChatsMeta = (response) =>
  response?.data?._meta ?? response?.data?.data?._meta ?? null;

/**
 * Чаты одного урока: их единицы, поэтому отдаются одним списком без пагинации.
 */
export const useLessonChats = (lessonId) => {
  const [request, setRequest] = useState({
    lessonId: null,
    chats: [],
    loading: false,
  });

  const reload = useCallback(() => {
    if (!lessonId) {
      setRequest({ lessonId: null, chats: [], loading: false });
      return undefined;
    }

    let cancelled = false;
    setRequest({ lessonId, chats: [], loading: true });
    chatApi.getLessonChats({ lesson_id: lessonId })
      .then((response) => {
        if (!cancelled) {
          setRequest({
            lessonId,
            chats: normalizeLessonChatsResponse(response) ?? [],
            loading: false,
          });
        }
      })
      .catch(() => {
        // Урок без блока чатов или без доступа — просто пустой список.
        if (!cancelled) {
          setRequest({ lessonId, chats: [], loading: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  useEffect(() => reload(), [reload]);

  const isCurrentLesson = String(request.lessonId) === String(lessonId);

  return {
    chats: isCurrentLesson ? request.chats : [],
    loading: Boolean(lessonId) && (!isCurrentLesson || request.loading),
    reload,
  };
};

/**
 * Глобальный раздел «Чаты уроков»: у администратора это весь каталог, только страницами.
 */
export const fetchLessonChatsPage = async ({ page = 1, perPage = 20 } = {}) => {
  const response = await chatApi.getLessonChats({ page, 'per-page': perPage });
  const items = normalizeLessonChatsResponse(response);

  if (items === null) {
    throw new Error('Не удалось загрузить чаты уроков');
  }

  return {
    items,
    meta: getLessonChatsMeta(response),
  };
};
